import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { getDesignTokens } from '~/client/components/base/core';
import { dashboardRouteUtil, settingsRoutesUtil } from '~/client/routes';

const BOTTOM_NAV_HEIGHT = 56;

const StyledPaper = styled(Paper)(({ theme }) => {
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );
  return {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: theme.zIndex.appBar,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: tokens.surface1,
    '@media print': {
      display: 'none',
    },
  };
});

const StyledBottomNavigation = styled(BottomNavigation)(({ theme }) => {
  const tokens = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );
  return {
    height: BOTTOM_NAV_HEIGHT,
    backgroundColor: 'transparent',
    '& .MuiBottomNavigationAction-root': {
      color: tokens.textSecondary,
      minWidth: 'auto',
      padding: '6px 12px 8px',
      '&.Mui-selected': {
        color: tokens.accent,
      },
    },
    '& .MuiBottomNavigationAction-label': {
      fontSize: '0.75rem',
      '&.Mui-selected': {
        fontSize: '0.75rem',
      },
    },
  };
});

type NavItem = 'tasks' | 'status' | 'projects' | 'assurance' | 'more';

const BottomNav: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { navigateTo, matchRoute } = dashboardRouteUtil.useHelper();
  const { navigateTo: navigateToSettings } = settingsRoutesUtil.useHelper();

  const getCurrentValue = (): NavItem => {
    const { pathname } = location;
    if (
      pathname.startsWith('/settings') ||
      pathname.startsWith('/org-admin') ||
      pathname.startsWith('/system-admin')
    ) {
      return 'more';
    }
    if (matchRoute('TASKS')) {
      return 'tasks';
    }
    if (matchRoute('STATUS')) {
      return 'status';
    }
    if (matchRoute('COMPLIANCE') || matchRoute('PROJECTS')) {
      return 'projects';
    }
    if (matchRoute('ASSURANCE')) {
      return 'assurance';
    }
    return 'tasks';
  };

  const [value, setValue] = React.useState<NavItem>(getCurrentValue());

  React.useEffect(() => {
    setValue(getCurrentValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleChange = (_event: React.SyntheticEvent, newValue: NavItem) => {
    setValue(newValue);
    switch (newValue) {
      case 'tasks':
        navigateTo('TASKS');
        break;
      case 'status':
        navigateTo('STATUS');
        break;
      case 'projects':
        navigateTo('PROJECTS');
        break;
      case 'assurance':
        navigateTo('ASSURANCE');
        break;
      case 'more':
        navigateToSettings('PERSONAL');
        break;
      default:
        break;
    }
  };

  if (!isMobile) {
    return null;
  }

  return (
    <StyledPaper elevation={3}>
      <StyledBottomNavigation value={value} onChange={handleChange} showLabels>
        <BottomNavigationAction
          label="Tasks"
          value="tasks"
          icon={<FormatListBulletedIcon />}
        />
        <BottomNavigationAction
          label="Status"
          value="status"
          icon={<BarChartOutlinedIcon />}
        />
        <BottomNavigationAction
          label="Projects"
          value="projects"
          icon={<AssignmentOutlinedIcon />}
        />
        <BottomNavigationAction
          label="Assurance"
          value="assurance"
          icon={<PlayArrowOutlinedIcon />}
        />
        <BottomNavigationAction
          label="More"
          value="more"
          icon={<MoreHorizIcon />}
        />
      </StyledBottomNavigation>
    </StyledPaper>
  );
};

export default BottomNav;
export { BOTTOM_NAV_HEIGHT };
